import { IBizEntity, IOptimaTreeNode } from '@optima-stride-ai/core';
import { Explorer, IExplorerAttributes } from '@optima-stride-ai/optima-components';
import { refreshExplorerNav } from '../../utils/explorer-nav-selection';
import { hasCompleteAncestorPath } from '../../utils/explorer-reveal';
import { Subject } from 'rxjs';

/** Independent creation channel; never consumes legacy state notifications. */
const creations = new Subject<IBizEntity>();
/** Non-replaying stream consumed only by non-modal Strategy Explorer. */
export const strategyEntityCreated$ = creations.asObservable();
/** Synchronous provenance for the unchanged legacy create-completion callback. */
let legacyCreationDepth = 0;

/**
 * Identifies a legacy callback associated with an additive Strategy event.
 * @returns Whether the synchronous creation callback is executing.
 * @example if (isStrategyCreationCompletion()) return;
 */
export function isStrategyCreationCompletion(): boolean {
  return legacyCreationDepth > 0;
}

/**
 * Adds Strategy refresh without replacing the original save-completion behavior.
 * @param entity - Submitted entity, including its actual parent and model instances.
 * @param response - Successful response containing bizEntityId.
 * @param completeLegacy - Original assignment, executed unchanged even for invalid IDs.
 * @returns Nothing; only valid server identities produce the additional event.
 * @example notifyStrategyEntityCreated(entity, response, () => state.incomingBizEntitySelectionId = id);
 */
export function notifyStrategyEntityCreated(entity: IBizEntity, response: unknown, completeLegacy?: () => void): void {
  const id = typeof response === 'object' && response !== null && !Array.isArray(response)
    ? (response as Record<string, unknown>)['bizEntityId'] : undefined;
  const valid = (typeof id === 'string' && id.trim().length > 0) ||
    (typeof id === 'number' && Number.isFinite(id));
  legacyCreationDepth++;
  try {
    if (valid) {
      creations.next({ ...entity, id: String(id),
        additionalInfo: entity.additionalInfo?.map(info => ({ ...info })) ?? entity.additionalInfo });
    }
    completeLegacy?.();
  } finally {
    legacyCreationDepth--;
  }
}

/**
 * Synchronizes Strategy Explorer's lazy hierarchy before rebuilding its navigation.
 * Both library projections use string identities so strict navigation lookups agree.
 * @param explorer - Mounted Strategy Explorer, if available.
 * @returns Nothing; updates this explorer's projections and navigation highlight.
 * @example refreshStrategyExplorerNav(strategyExplorer);
 */
export function refreshStrategyExplorerNav(explorer: Explorer | undefined): void {
  const attributes = explorer?.explorerAttributes;
  if (explorer && attributes) {
    // Project rather than mutate cached records. Nested models (including type metadata)
    // retain their instances and prototypes through these shallow copies.
    attributes.data = projectExplorerEntities(attributes.data);
    const navAttributes = attributes.explorerNavAttributes;
    if (navAttributes) {
      navAttributes.data = projectExplorerEntities(navAttributes.data);
      // Host inputs may not yet have propagated through Angular to the mounted nav.
      explorer.explorerNavAttributes = navAttributes;
      if (explorer.explorerNav) {
        explorer.explorerNav.explorerNavAttributes = navAttributes;
      }
    }
  }
  refreshExplorerNav(explorer);
}

/**
 * Creates a deduplicated library projection without modifying source records.
 * Nullish root parents retain their root sentinel; all other identities become strings.
 * @param entities - Source rows for either the full explorer or its filtered navigation.
 * @returns Shallow copies keyed by String(id), with the last duplicate winning.
 * @example const rows = projectExplorerEntities(cachedEntities);
 */
function projectExplorerEntities(entities: IBizEntity[]): IBizEntity[] {
  const byId = new Map<string, IBizEntity>();
  for (const entity of entities) {
    const id = String(entity.id);
    const parentId = entity.parentId == null ? entity.parentId : String(entity.parentId);
    byId.set(id, { ...entity, id, parentId });
  }
  return [...byId.values()];
}

/**
 * Expands an entity's tree path without navigating or changing the selected node.
 * @param explorer - Mounted Strategy Explorer, if available.
 * @param entityId - Newly created entity (or its parent for content-only entities).
 * @returns Whether the path was present in the navigation tree.
 * @example expandExplorerNavPath(explorer, createdEntity.id);
 */
export function expandExplorerNavPath(explorer: Explorer | undefined, entityId: string): boolean {
  const path = findNodePath(explorer?.explorerNav?.hierarchyNodes ?? [], String(entityId));
  path?.forEach(node => node.expanded = true);
  return !!path;
}

/**
 * Strategy-specific adapters for the selection-preserving creation refresh.
 * @example await refreshCreatedExplorerEntity({ entity, attributes, ...adapters });
 */
export interface ExplorerCreationRefresh {
  /** Successfully persisted entity with its authoritative server ID and submitted parent. */
  entity: IBizEntity;
  /** Current explorer configuration, read again after asynchronous work. */
  attributes: IExplorerAttributes;
  /** Mounted library explorer. */
  explorer: Explorer | undefined;
  /** Parent-scoped pagination operations; clear also invalidates in-flight page generations. */
  buffer: {
    /** Reads the currently cached page depth for this parent. */
    getItems(parentId: string): IBizEntity[];
    /** Invalidates this parent's buffered pages and in-flight page generations. */
    clear(parentId: string): void;
  };
  /** Prevents late network responses from mutating a destroyed explorer. */
  isActive: () => boolean;
  /** Merges records using this explorer's category filters and ID deduplication. */
  append: (entities: IBizEntity[]) => void;
  /** Reads the missing parent path, not all siblings or every content page. */
  loadAncestors: () => Promise<IBizEntity[]>;
  /** Reloads only an affected, currently viewed parent, retaining its loaded page depth. */
  refreshContent: (parentId: string, loadedCount: number) => Promise<void>;
  /** Invalidates any additional parent-specific cursor and unbuffered content requests. */
  invalidate?: (parentId: string) => void;
  /** Reports partial refresh failures without reclassifying a successful save as failed. */
  onError: (error: unknown) => void;
}

/**
 * Reconciles a confirmed creation independently of explicit selection/navigation requests.
 * Keeps unloaded/paginated parents discoverable and removes stale empty-folder metadata.
 * @param options - Strategy-specific cache and loading adapters.
 * @returns Completion of the affected content refresh; failures are reported through onError.
 * @example await refreshCreatedExplorerEntity({ entity, attributes, ...adapters });
 */
export async function refreshCreatedExplorerEntity(options: ExplorerCreationRefresh): Promise<void> {
  if (!options.isActive()) return;
  const { entity, attributes, buffer } = options;
  const parentId = entity.parentId?.toString() ?? '';
  const loadedCount = buffer.getItems(parentId).length;
  buffer.clear(parentId);
  options.invalidate?.(parentId);
  // A prior navigation hint must not navigate again when the tree is rebuilt.
  attributes.preSelectedItemId = '';
  const nav = attributes.explorerNavAttributes;
  if (nav) {
    nav.knownEmptyFolderIds = nav.knownEmptyFolderIds?.filter(id => id !== parentId);
  }
  options.append([entity]);

  if (parentId && !hasCompleteAncestorPath(attributes.data, parentId)) {
    try {
      const ancestors = await options.loadAncestors();
      if (!options.isActive()) return;
      options.append(ancestors);
    } catch (error) {
      if (options.isActive()) options.onError(error);
    }
  }
  if (!options.isActive()) return;
  refreshStrategyExplorerNav(options.explorer);
  if (!expandExplorerNavPath(options.explorer, entity.id)) {
    expandExplorerNavPath(options.explorer, parentId);
  }
  const selectedParent = attributes.explorerNavAttributes?.currentSelectedItem
    || attributes.explorerContentAttributes?.currentSelectedItem || '';
  if (selectedParent.toString() === parentId) {
    try {
      await options.refreshContent(parentId, loadedCount);
    } catch (error) {
      if (options.isActive()) options.onError(error);
    }
  }
}

/**
 * Finds the root-to-entity path in the current navigation tree.
 * @param nodes - Tree nodes at the current hierarchy level.
 * @param id - Canonical string identity to locate.
 * @returns The complete path, or null when the entity is absent.
 * @example const path = findNodePath(nav.hierarchyNodes, createdEntity.id);
 */
function findNodePath(nodes: IOptimaTreeNode[], id: string): IOptimaTreeNode[] | null {
  for (const node of nodes) {
    if (node.data?.id?.toString() === id) {
      return [node];
    }
    const childPath = node.children?.length ? findNodePath(node.children, id) : null;
    if (childPath) {
      return [node, ...childPath];
    }
  }
  return null;
}